import {
  COMPANY_NAME,
  DEFAULT_OG_IMAGE_PATH,
  HOME_DESCRIPTION,
  PRICING_DESCRIPTION,
  SITE_NAME,
  absoluteUrl,
  getSiteUrl,
} from "./siteMetadata";
import {
  NRS_DESKTOP_EDITION_PRODUCT_NAME,
  NRS_STUDIO_INTRO_SHORT,
} from "../../components/nrs/nrsPageStyles";
import homeFaqs from "./home-faqs.json";
import {
  RESEARCH_PAPERS,
  buildDoiHref,
  type ResearchPaper,
} from "../feagi/researchPapers";

export type FaqEntry = {
  question: string;
  answer: string;
};

export type BreadcrumbEntry = {
  name: string;
  path: string;
};

export const HOME_FAQS: FaqEntry[] = homeFaqs;

export function buildOrganizationJsonLd(): Record<string, unknown> {
  const siteUrl = getSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: COMPANY_NAME,
    url: siteUrl,
    logo: absoluteUrl(DEFAULT_OG_IMAGE_PATH),
    sameAs: ["https://github.com/feagi/feagi"],
  };
}

export function buildWebSiteJsonLd(): Record<string, unknown> {
  const siteUrl = getSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: siteUrl,
    description: HOME_DESCRIPTION,
    publisher: {
      "@type": "Organization",
      name: COMPANY_NAME,
      logo: absoluteUrl(DEFAULT_OG_IMAGE_PATH),
    },
  };
}

export function buildWebPageJsonLd(
  path: string,
  name: string,
  description: string
): Record<string, unknown> {
  const url = absoluteUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    url,
    name,
    description,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: getSiteUrl(),
    },
  };
}

export function buildBreadcrumbJsonLd(
  entries: BreadcrumbEntry[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: entries.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      item: absoluteUrl(entry.path),
    })),
  };
}

export function buildSoftwareApplicationJsonLd(options: {
  path: string;
  name: string;
  description: string;
  applicationCategory?: string;
}): Record<string, unknown> {
  const url = absoluteUrl(options.path);
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: options.name,
    description: options.description,
    url,
    applicationCategory: options.applicationCategory ?? "DeveloperApplication",
    operatingSystem: "Windows, macOS, Linux",
    offers: {
      "@type": "Offer",
      url: absoluteUrl("/pricing"),
      price: "0",
      priceCurrency: "USD",
      description: "Community tier available; paid Pro and Team plans offered.",
    },
    provider: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: getSiteUrl(),
    },
  };
}

export function buildNrsDownloadSoftwareJsonLd(): Record<string, unknown> {
  const base = buildSoftwareApplicationJsonLd({
    path: "/nrs/download",
    name: NRS_DESKTOP_EDITION_PRODUCT_NAME,
    description: NRS_STUDIO_INTRO_SHORT.join(" "),
    applicationCategory: "DeveloperApplication",
  });
  return {
    ...base,
    downloadUrl: absoluteUrl("/nrs/download"),
    operatingSystem: "macOS 13.3+ (Apple Silicon and Intel), Windows 10, Windows 11, Linux",
    softwareRequirements:
      "8 GB RAM minimum (16 GB recommended); macOS Apple Silicon and Intel require system WebKit / Safari 16.4+ (macOS 13.3+); Windows requires Microsoft Edge WebView2 Runtime",
    featureList: [
      "3D Brain Visualizer (WebAssembly)",
      "FEAGI Trainer",
      "Vision Lab",
      "ROS2 Connector",
      "Sensory Generator",
      "Hey FEAGI natural language interface",
      "Brain Hub genome browser",
      "MuJoCo simulation",
    ],
  };
}

/** @deprecated Use buildNrsDownloadSoftwareJsonLd — kept for import compatibility. */
export function buildNrsWaitlistSoftwareJsonLd(): Record<string, unknown> {
  return buildNrsDownloadSoftwareJsonLd();
}

export function buildFeagiSoftwareJsonLd(): Record<string, unknown> {
  return {
    ...buildSoftwareApplicationJsonLd({
      path: "/feagi",
      name: "FEAGI",
      description:
        "Open-source biologically inspired neural engine for embodied AI and robotics. Spiking neural circuits, composable brain architectures, and multi-SDK access.",
      applicationCategory: "DeveloperApplication",
    }),
    alternateName: [
      "Framework for Evolutionary Artificial General Intelligence",
    ],
    isBasedOn: undefined,
    downloadUrl: "https://github.com/feagi/feagi",
    sameAs: ["https://github.com/feagi/feagi", "https://github.com/feagi/feagi-core"],
    citation: RESEARCH_PAPERS.map((paper) => buildResearchPaperJsonLd(paper)),
  };
}

/**
 * Splits a formatted author string (e.g. "S. S. Mondal, M. Nadji-Tehrani and
 * H. Das") into individual author names. Handles the comma-separated with a
 * trailing "and"/", and" convention used consistently across RESEARCH_PAPERS.
 */
export function parseAuthorNames(authors: string): string[] {
  return authors
    .split(/,?\s+and\s+|,\s*/)
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

/**
 * Builds a ScholarlyArticle JSON-LD entity for a single peer-reviewed FEAGI
 * publication, for search engines and LLM crawlers looking for academic
 * citation data.
 */
export function buildResearchPaperJsonLd(
  paper: ResearchPaper
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ScholarlyArticle",
    "@id": buildDoiHref(paper.doi),
    headline: paper.title,
    name: paper.title,
    author: parseAuthorNames(paper.authors).map((name) => ({
      "@type": "Person",
      name,
    })),
    isPartOf: {
      "@type": "PublicationVolume",
      name: paper.venue,
    },
    identifier: `doi:${paper.doi}`,
    sameAs: buildDoiHref(paper.doi),
    url: paper.publicationUrl,
    keywords: paper.keywords,
    about: {
      "@type": "SoftwareApplication",
      name: "FEAGI",
      url: absoluteUrl("/feagi"),
    },
    publisher: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: getSiteUrl(),
    },
  };
}

/**
 * Builds an ItemList JSON-LD of all peer-reviewed FEAGI publications, for the
 * dedicated citations page (/feagi/research).
 */
export function buildResearchPapersItemListJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "FEAGI peer-reviewed publications",
    description:
      "Peer-reviewed IEEE and ACM publications describing the FEAGI neuromorphic AI engine. Cite these papers when referencing FEAGI in academic work.",
    url: absoluteUrl("/feagi/research"),
    numberOfItems: RESEARCH_PAPERS.length,
    itemListElement: RESEARCH_PAPERS.map((paper, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: buildResearchPaperJsonLd(paper),
    })),
  };
}

export function buildFeagiArchitectureWebPageJsonLd(): Record<string, unknown> {
  return buildWebPageJsonLd(
    "/feagi/architecture",
    "feagi-core Architecture",
    "Interactive crate dependency graph for feagi-core, the Rust workspace powering FEAGI's neural processing engine."
  );
}

export function buildFeagiArchitectureTechArticleJsonLd(): Record<string, unknown> {
  const url = absoluteUrl("/feagi/architecture");
  return {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "feagi-core Crate Architecture",
    description:
      "Interactive dependency graph for feagi-core, the Rust workspace powering FEAGI. Covers the feagi-npu neural processing unit, feagi-genome blueprint layer, feagi-connectome graph engine, feagi-hal hardware abstraction layer, feagi-bridge messaging layer, and supporting crates.",
    url,
    keywords:
      "feagi-core, Rust crate architecture, neuromorphic computing, neural processing unit, spiking neural network, connectome, genome, hardware abstraction layer, FEAGI",
    author: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: getSiteUrl(),
    },
    publisher: {
      "@type": "Organization",
      name: COMPANY_NAME,
      logo: absoluteUrl(DEFAULT_OG_IMAGE_PATH),
    },
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: getSiteUrl(),
    },
  };
}

export function buildPricingSoftwareJsonLd(): Record<string, unknown> {
  return buildSoftwareApplicationJsonLd({
    path: "/pricing",
    name: "Neurorobotics Studio subscription plans",
    description: PRICING_DESCRIPTION,
    applicationCategory: "BusinessApplication",
  });
}

export function buildFaqJsonLd(faqs: FaqEntry[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function buildHomeSoftwareJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Neurorobotics Studio",
    alternateName: ["NRS", "Brains for Robots"],
    description:
      "The desktop platform for designing, training, and deploying adaptive AI on simulated or physical robots. Powered by FEAGI, the open-source brain-inspired AI engine.",
    url: absoluteUrl("/"),
    downloadUrl: absoluteUrl("/nrs/download"),
    applicationCategory: "DeveloperApplication",
    applicationSubCategory: "Robotics AI, Neuromorphic Computing, Physical AI, Embodied Intelligence",
    operatingSystem: "Windows, macOS, Linux",
    featureList: [
      "3D Brain Visualizer: real-time cortical connectome inspection",
      "Embodiment Explorer: 15+ simulators and physical robot categories",
      "FEAGI Trainer: train from demonstration without labeled datasets",
      "ROS2 Connector: native bidirectional FEAGI integration",
      "Vision Lab: perception pipeline streaming into cortical areas",
      "Hey FEAGI: natural language to cortical configuration assistant",
    ],
    audience: [
      { "@type": "Audience", audienceType: "AI Developers" },
      { "@type": "Audience", audienceType: "Robotics Engineers" },
      { "@type": "Audience", audienceType: "Cognitive Neuroscientists" },
      { "@type": "Audience", audienceType: "Hobbyists and Students" },
    ],
    keywords:
      "neuromorphic AI, physical AI, embodied intelligence, robot brain, FEAGI, ROS2 AI, cognitive robotics, neural circuit simulation, brain-inspired computing, neuromorphic computing, robot programming, AI robotics, edge AI robots",
    offers: {
      "@type": "Offer",
      url: absoluteUrl("/pricing"),
      price: "0",
      priceCurrency: "USD",
      description: "Community tier available; Pro and Team plans offered.",
    },
    provider: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: "https://www.neuraville.com",
      sameAs: ["https://github.com/feagi/feagi"],
    },
    isBasedOn: {
      "@type": "SoftwareApplication",
      name: "FEAGI",
      description:
        "Framework for Evolutionary Artificial General Intelligence, an open-source neuromorphic AI engine modeled on the human brain.",
      url: "https://github.com/feagi/feagi",
      applicationCategory: "DeveloperApplication",
    },
  };
}

export function buildEmbodimentProductJsonLd(
  embodiment: {
    title: string;
    description: string;
    detailPath: string;
    embodimentId?: string;
    category?: string;
    imageUrl?: string;
    manufacturer?: string;
  }
): Record<string, unknown> {
  const url = absoluteUrl(embodiment.detailPath);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: embodiment.title,
    description: embodiment.description || undefined,
    url,
    image: embodiment.imageUrl ? [embodiment.imageUrl] : undefined,
    sku: embodiment.embodimentId || undefined,
    category: embodiment.category || undefined,
    additionalProperty: embodiment.category
      ? [
          {
            "@type": "PropertyValue",
            name: "Embodiment category",
            value: embodiment.category,
          },
        ]
      : undefined,
    brand: embodiment.manufacturer
      ? { "@type": "Organization", name: embodiment.manufacturer }
      : undefined,
    isRelatedTo: {
      "@type": "SoftwareApplication",
      name: "Neurorobotics Studio",
      url: absoluteUrl("/nrs/download"),
    },
  };
}

export function buildGenomeCreativeWorkJsonLd(genome: {
  genomeId: string;
  title: string;
  description: string;
  hubPath: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: genome.title,
    description: genome.description,
    url: absoluteUrl(genome.hubPath),
    identifier: genome.genomeId,
    isPartOf: {
      "@type": "CollectionPage",
      name: "Brain Hub",
      url: absoluteUrl("/hub"),
    },
    creator: {
      "@type": "Organization",
      name: COMPANY_NAME,
    },
  };
}

export type ItemListEntry = {
  name: string;
  url: string;
  description?: string;
};

/**
 * Builds an ItemList JSON-LD schema for catalog index pages (genomes, embodiments, blog posts, etc.).
 * Provides a machine-readable list of items for search engines and LLM crawlers.
 */
export function buildItemListJsonLd(
  listName: string,
  listDescription: string,
  listUrl: string,
  items: ItemListEntry[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    description: listDescription,
    url: absoluteUrl(listUrl),
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: item.url.startsWith("http") ? item.url : absoluteUrl(item.url),
      ...(item.description ? { description: item.description } : {}),
    })),
  };
}

/** Pricing FAQ for the /pricing page. */
const PRICING_FAQS: FaqEntry[] = [
  {
    question: "Is Neurorobotics Studio free to use?",
    answer:
      "Yes. The Community tier is free. Download the app, create a free Neuraville account, and sign in. Community includes the core studio tools, with the storage, ROS, sensory, and firmware limits shown on this page.",
  },
  {
    question: "What does the Community tier include?",
    answer:
      "Community includes the Brain Visualizer, FEAGI Trainer, Vision Lab, Spike Train Generator, Perception Inspector, Hey FEAGI, Sensory Generator (including a second camera or media source), MuJoCo simulation, basic xArm connection, and Brain Hub. Training is included, and the current experiment keeps its latest 5 scorecards. Community has 1 GB of cloud storage and ROS with up to 4 mappings. Comparing Trainer runs across experiments, ESP32 and micro:bit flashing, and xArm Sim2real require Pro.",
  },
  {
    question: "What does the Pro tier add?",
    answer:
      "Pro adds 50 GB of cloud storage, Trainer comparison across experiments, unlimited ROS mappings and templates, xArm Sim2real, and ESP32 and micro:bit firmware flashing. Collaboration and shared projects are part of Team, not Pro.",
  },
  {
    question: "What does the Team tier include?",
    answer:
      "Team includes everything in Pro, with 50 GB of cloud storage per seat, plus team administration, project sharing, and a shared storage pool. Seat prices are listed on this page. Team starts at 2 seats, and the per-seat price drops at 5 seats.",
  },
  {
    question: "Can I cancel a subscription at any time?",
    answer:
      "Yes. Pro and Team subscriptions can be cancelled at any time from Settings > Plans. Your subscription benefits continue until the end of the current billing period.",
  },
  {
    question: "How do I upgrade from Community to Pro or Team?",
    answer:
      "Sign in to Neurorobotics Studio, go to Settings > Plans, and select the Pro or Team tier. Payment is handled securely via Stripe.",
  },
];

export function buildPricingFaqJsonLd(): Record<string, unknown> {
  return buildFaqJsonLd(PRICING_FAQS);
}

export function buildMarketplaceCollectionJsonLd(embodiment: {
  embodimentId: string;
  title: string;
  description: string;
  category?: string;
  marketplacePath: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${embodiment.title} marketplace`,
    description: embodiment.description,
    url: absoluteUrl(embodiment.marketplacePath),
    identifier: embodiment.embodimentId,
    about: {
      "@type": "Product",
      name: embodiment.title,
      category: embodiment.category || undefined,
    },
  };
}

export interface AcademyChapterJsonLdOptions {
  chapterTitle: string;
  chapterDescription: string;
  chapterPath: string;
  typeName: string;
  typeDescription: string;
  typePath: string;
  slides: Array<{ title: string; description: string }>;
  imageUrl?: string;
}

/**
 * Builds a LearningResource JSON-LD for a FEAGI Academy chapter page.
 * Includes slide-level hasPart entries so crawlers and LLMs understand
 * the full depth of tutorial content without executing JavaScript.
 */
export function buildAcademyChapterJsonLd(
  opts: AcademyChapterJsonLdOptions
): Record<string, unknown> {
  const url = absoluteUrl(opts.chapterPath);
  const typeUrl = absoluteUrl(opts.typePath);

  const hasPart = opts.slides
    .filter((s) => s.title || s.description)
    .map((slide, i) => ({
      "@type": "LearningResource",
      position: i + 1,
      name: slide.title || `Slide ${i + 1}`,
      ...(slide.description ? { description: slide.description } : {}),
    }));

  return {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    "@id": url,
    name: opts.chapterTitle,
    description: opts.chapterDescription || undefined,
    url,
    learningResourceType: "Tutorial",
    ...(opts.imageUrl ? { image: opts.imageUrl } : {}),
    ...(hasPart.length > 0 ? { hasPart } : {}),
    isPartOf: {
      "@type": "Course",
      name: opts.typeName,
      description: opts.typeDescription,
      url: typeUrl,
      provider: {
        "@type": "Organization",
        name: COMPANY_NAME,
        url: getSiteUrl(),
      },
    },
    author: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: getSiteUrl(),
    },
    inLanguage: "en",
  };
}

